/**
 * RequestsToolbar Component
 *
 * Toolbar for filtering and searching ticket requests.
 * Shows summary stats and filter controls.
 *
 * Created: 2026-01-13
 */

import React from 'react';
import { Search, Filter, RefreshCw, X, Archive } from 'lucide-react';
import { useTicketRequestsStore } from '../../stores/ticketRequestsStore';
import type { RequestStats } from '../../stores/ticketRequestsStore';

interface RequestsToolbarProps {
  stats: RequestStats | null;
}

export const RequestsToolbar: React.FC<RequestsToolbarProps> = ({ stats }) => {
  const {
    filters,
    isLoading,
    setFilter,
    setSearch,
    clearFilters,
    fetchRequests,
  } = useTicketRequestsStore();

  // Status options for filter
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'requested', label: 'Requested' },
    { value: 'approved', label: 'Approved' },
    { value: 'denied', label: 'Denied' },
    { value: 'waitlist', label: 'Waitlist' },
    { value: 'fulfilled', label: 'Fulfilled' },
  ];

  const hasActiveFilters = filters.status || filters.event_id || filters.search || filters.date_from || filters.date_to || filters.include_archived;

  return (
    <div data-testid="requests-toolbar" className="border-b border-dark-border/30 bg-dark-surface/50 px-4 py-3">
      {/* Stats row */}
      {stats && (
        <div className="mb-3 flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-dark-muted">Total:</span>
            <span className="font-medium text-white">{stats.total_requests}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-yellow-400" />
            <span className="text-yellow-400">{stats.pending_count} pending</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-orange-400" />
            <span className="text-orange-400">{stats.requested_count} requested</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-400" />
            <span className="text-green-400">{stats.approved_count} approved</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-purple-400" />
            <span className="text-purple-400">{stats.total_tickets_requested} tickets requested</span>
          </div>
        </div>
      )}

      {/* Filter controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search input */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dark-muted" />
          <input
            type="text"
            placeholder="Search by name, company, or email..."
            value={filters.search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
            className="w-full rounded-lg border border-dark-border/30 bg-dark-input pl-10 pr-4 py-2 text-sm text-white placeholder-dark-muted focus:border-purple-500 focus:outline-none"
          />
          {filters.search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark-text"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-dark-muted" />
          <select
            value={filters.status || ''}
            onChange={(e) => setFilter('status', e.target.value || null)}
            data-testid="status-filter"
            className="rounded-lg border border-dark-border/30 bg-dark-input px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Show Archived toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.include_archived}
            onChange={(e) => setFilter('include_archived', e.target.checked)}
            data-testid="show-archived-toggle"
            className="h-4 w-4 rounded border-dark-border/30 bg-dark-input text-purple-600 focus:ring-purple-500 focus:ring-offset-dark-bg"
          />
          <span className="flex items-center gap-1.5 text-sm text-dark-text">
            <Archive className="h-4 w-4" />
            Show Archived
          </span>
        </label>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            data-testid="clear-filters-btn"
            className="flex items-center gap-1.5 rounded-lg border border-dark-border/30 px-3 py-2 text-sm text-dark-text hover:bg-dark-input"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </button>
        )}

        {/* Refresh button */}
        <button
          onClick={fetchRequests}
          disabled={isLoading}
          data-testid="refresh-btn"
          className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
    </div>
  );
};
